import {
  ChangeDetectionStrategy, ChangeDetectorRef, Component, Input, NgZone, OnChanges, OnDestroy, SimpleChanges,
} from '@angular/core';
import { CommonModule } from '@angular/common';

interface Glyph { key: string; digit: boolean; d: number; ch: string; }

/**
 * Live number (LIVE_UPDATES_NO_POLLING_PLAN.md, section 6, 2026-09-28).
 * Shows a number; when the value changes it rolls ONLY the digits that changed, like an odometer,
 * from the old value to the new one (never from 0), and floats a small ▲ +x / ▼ −x for ~2 s.
 * First render never animates. "Reduce motion" → no rolling, colour glow only.
 *
 * <app-live-number [value]="lot.currentCbm" [minDecimals]="2" [maxDecimals]="3"></app-live-number>
 */
@Component({
  selector: 'app-live-number',
  standalone: true,
  imports: [CommonModule],
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <span class="ln" [class.ln--anim]="animate" [class.ln--up]="flash === 'up'" [class.ln--down]="flash === 'down'"
          [attr.aria-label]="text">
      <span class="ln-digits" aria-hidden="true"><ng-container *ngFor="let g of glyphs; trackBy: trackGlyph"><span
          *ngIf="!g.digit" class="ln-sym">{{ g.ch }}</span><span *ngIf="g.digit" class="ln-reel"><span class="ln-strip"
          [style.transform]="'translateY(' + (-g.d * 10) + '%)'"><span *ngFor="let n of DIGITS">{{ n }}</span></span></span></ng-container></span>
      <span *ngIf="deltaText" class="ln-delta" [class.ln-delta--up]="flash === 'up'" [class.ln-delta--down]="flash === 'down'"
            aria-hidden="true">{{ flash === 'up' ? '▲' : '▼' }} {{ deltaText }}</span>
    </span>
  `,
  styleUrls: ['./live-number.component.css'],
})
export class LiveNumberComponent implements OnChanges, OnDestroy {
  @Input() value: number | null | undefined = null;
  @Input() minDecimals = 0;
  @Input() maxDecimals = 3;
  /** Show the floating ▲/▼ difference on change. */
  @Input() showDelta = true;
  /** Shown instead of the number when value is null. */
  @Input() empty = '—';
  /** Change this (e.g. the unit CBM / CFT) to show the new value without animating — a unit or
   *  format switch is not a data change. */
  @Input() resetKey: unknown = null;

  readonly DIGITS = [0, 1, 2, 3, 4, 5, 6, 7, 8, 9];
  glyphs: Glyph[] = [];
  text = '';
  animate = false;
  flash: 'up' | 'down' | null = null;
  deltaText = '';
  private prev: number | null = null;
  private timers: ReturnType<typeof setTimeout>[] = [];

  constructor(private cdr: ChangeDetectorRef, private zone: NgZone) {}

  ngOnChanges(ch: SimpleChanges): void {
    const v = this.value == null || isNaN(Number(this.value)) ? null : Number(this.value);
    this.text = v == null ? this.empty : this.format(v);
    this.glyphs = this.toGlyphs(this.text);
    const first = ch['value']?.firstChange;
    const reset = !!ch['resetKey'] && !ch['resetKey'].firstChange;
    if (!first && !reset && this.prev != null && v != null && v !== this.prev) {
      const diff = v - this.prev;
      this.flash = diff > 0 ? 'up' : 'down';
      this.deltaText = this.showDelta ? this.format(Math.abs(diff)) : '';
      this.later(() => { this.flash = null; this.deltaText = ''; }, 2200);
    }
    if (first) {
      // Enable the rolling transition only after the first value is on screen.
      this.later(() => { this.animate = true; }, 50);
    }
    this.prev = v;
  }

  ngOnDestroy(): void {
    this.timers.forEach(t => clearTimeout(t));
  }

  trackGlyph = (_: number, g: Glyph) => g.key;

  private format(v: number): string {
    return v.toLocaleString('en-IN', { minimumFractionDigits: this.minDecimals, maximumFractionDigits: this.maxDecimals });
  }

  /** Keys are anchored on the decimal point (integer digits counted leftwards, decimals rightwards),
   *  so each digit keeps its reel when the number grows or gains a decimal — only changed digits roll. */
  private toGlyphs(text: string): Glyph[] {
    const chars = [...text];
    const dotAt = chars.indexOf('.') >= 0 ? chars.indexOf('.') : chars.length;
    return chars.map((ch, i) => {
      const pos = i < dotAt ? `i${dotAt - i}` : i === dotAt ? 'dot' : `f${i - dotAt}`;
      const digit = ch >= '0' && ch <= '9';
      return { key: `${pos}${digit ? 'd' : ch}`, digit, d: digit ? +ch : 0, ch };
    });
  }

  /** One-off UI delays (not polling) — run outside Angular, then mark for check. */
  private later(fn: () => void, ms: number): void {
    this.zone.runOutsideAngular(() => {
      const t = setTimeout(() => this.zone.run(() => { fn(); this.cdr.markForCheck(); }), ms);
      this.timers.push(t);
    });
  }
}
